/* @flow */
import React, { useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import loadable from '@loadable/component';
import {
	BreadcrumbsWrapper,
	ContentSliderWrapper,
	DescriptionStatsWrapper,
	DescriptionTitle,
	FormWrapper,
	MapWrapper,
	MortgageWrapper,
	PropsWrapper,
	Root,
	Section,
	TitleMax2,
	TitleWithLogo,
} from '../ApartPage/styles';
import { Container } from '../Layout/styles';
import { formatValuesNumber, getApartRoomsAndSquareString } from '../../utils/formatting';
import Breadcrumbs from '../Breadcrumbs';
import {
	BottomRelatedRow,
	BottomWrapper,
	LogoImg,
	LogoText,
	LogoWrapper,
	MainGrid,
	MapBlock,
	RelatedTab,
	RelatedTabs,
	RelatedWrapper,
	ViewedWrapper,
} from './styles';
import type { TCities, TCity } from '../../types/city';
import Props from './Components/Props';
import { SkeletonComplexImageSlider, SkeletonMap } from '../Skeleton';
import { useModal } from '../../utils/hooks';
import type { TDispatch } from '../../types/core';
import { Logo } from '../Icons';
import Relevance from '../ApartPage/Components/Relevance';
import type { TMortgageFormCallbackData } from '../../types/mortgage';
import type { TRootState } from '../../types/rootState';
import CallbackPopupSwitcher from '../CallbackPopupSwitcher';
import { handleScrollTo } from '../../DOMUtils';
import type { TFilterBase } from '../../types/filters';
import { CLEAR_ACTION_CALLBACK_POPUP } from '../../constants/popup';
import Advert from './Components/Advert/Advert';
import HouseProps from './Components/HouseProps';
import ApartTitle from '../ApartPage/Components/ApartTitle';
import StickyBar from '../StickyBar';
import CompareShareBtns from '../CompareShareBtns';
import type { TApartment } from '../../reducers/apartment';
import { getCityRegionTitleById } from '../../actions/city';
import { setApartCurrentModal } from '../../actions/apartment';

const Modal = loadable(() => import('../Modal'), { fallback: null, ssr: false });
const ImageSlider = loadable(() => import('../ImageSlider'), {
	fallback: <SkeletonComplexImageSlider />,
});
const InfrastructureMap = loadable(() => import('../InfrastructureMap'), {
	ssr: true,
	fallback: <SkeletonMap />,
});
const DescriptionCharts = loadable(() => import('../DescriptionCharts'), { fallback: null, ssr: false });
const Mortgage = loadable(() => import('../Mortgage'), { fallback: null });
const Related = loadable(() => import('./Components/Related'), { fallback: null, ssr: true });

const ApartResale = () => {
	const dispatch: TDispatch = useDispatch();
	const isMobile: boolean = useSelector((state: TRootState) => state.app.isMobile);
	const apartment: TApartment = useSelector((state: TRootState) => state.apartment);
	const city: TCity = useSelector((state: TRootState) => state.city);
	const cities: TCities = useSelector((state: TRootState) => state.cities);
	const filterBase: TFilterBase = useSelector((state: TRootState) => state.filterBase);

	const { apartDataResale: apartData, apartAdvert, currentModal } = apartment;

	const { toggle } = useModal();
	const [mortgageModalData, setMortgageModalData] = useState<TMortgageFormCallbackData>({
		defaultPrice: apartData.priceTotalMin,
		initialFeeState: filterBase.mortgageInfo.initialFee,
		loanPeriodState: filterBase.mortgageInfo.loanPeriod,
		activeConfirmType: {
			value: null,
			label: null,
		},
	});
	const [activeTab, setActiveTab] = useState(apartData?.similar?.length > 0 ? 'similar' : 'lastViewed');

	const { isApartHotel, house, onMap, infrastructure, isActive } = apartData;
	const apartName = getApartRoomsAndSquareString(apartData.roomsCnt, apartData.areaTotal);
	const isApart = isApartHotel ? ' в Апарт-отеле' : ' в ЖК';
	const apartTitle = `${apartName}${house?.name !== null ? `${isApart} «${house.name}»` : ''}`;

	const breadcrumbs = [
		{
			index: 1,
			title: city.title,
			link: '/',
		},
		{
			index: 2,
			title: apartName,
		},
	];

	const withMap = house?.longitude && house?.latitude && onMap;

	const closeModal = () => {
		toggle();
		dispatch(setApartCurrentModal(null));
		dispatch({ type: CLEAR_ACTION_CALLBACK_POPUP });
	};

	// eslint-disable-next-line consistent-return
	const handlerModalToggle = (modalName: string) => {
		dispatch(setApartCurrentModal(modalName));
		toggle();
	};

	const handleMortgageClick = (mortgageData) => {
		setMortgageModalData(mortgageData);
		handlerModalToggle('mortgage');
	};

	const getPropValue = (value, mode, custom) => {
		if (value !== null) {
			if (mode) {
				return formatValuesNumber(value, mode, custom);
			}
			return value;
		}
		return false;
	};

	const PROPS_APART = [
		{
			label: 'Площадь',
			value: getPropValue(apartData.areaTotal, 'square'),
			isBig: true,
		},
		{
			label: 'Кухня',
			value: getPropValue(apartData.areaKitchen, 'square'),
			isBig: true,
		},
		{
			label: 'Этаж',
			value: getPropValue(apartData.floor, 'custom', apartData?.house?.floorsCnt ? `из ${house.floorsCnt}` : ''),
			isBig: true,
		},
		{
			label: 'Потолки',
			value: getPropValue(apartData.ceilingHeight, 'custom', 'м'),
			isBig: true,
		},
		{
			label: 'Санузел',
			value: getPropValue(apartData.bathroom),
		},
		{
			label: 'Балкон',
			value: getPropValue(apartData.balcony),
		},
	];

	const PROPS_BUILDING = [
		{
			title: 'Год постройки',
			value: getPropValue(house.builtYear),
		},
		{
			title: 'Количество этажей',
			value: getPropValue(house.floorsCnt),
		},
		{
			title: 'Серия дома',
			value: getPropValue(house.series),
		},
		{
			title: 'Аварийность дома',
			value: getPropValue(house.hasProblem),
		},
		{
			title: 'Тип дома',
			value: getPropValue(house.type),
		},
		{
			title: 'Технология строительства',
			value: getPropValue(house.technology),
		},
		{
			title: 'Балконы/лоджии',
			value: getPropValue(house.balconyType),
		},
		{
			title: 'Мусоропровод',
			value: getPropValue(house.garbageType),
		},
		{
			title: 'Управляющая компания',
			value: getPropValue(house.managementCompany),
		},
	];

	const hasHouseInfo = PROPS_BUILDING.some((item) => item.value !== false);

	const mapRef = useRef();

	const handleMapClick = () => {
		mapRef.current.scrollIntoView({
			behavior: 'smooth',
		});
	};

	const handleScrollToMortgage = () => {
		handleScrollTo('mortgageBlock', {
			delay: 0,
			smooth: true,
			offset: 0,
		});
	};

	const cityTitle = getCityRegionTitleById(house.cityId, cities);

	return (
		<Root>
			<Section isGray>
				<Container>
					{!isMobile && (
						<BreadcrumbsWrapper>
							<Breadcrumbs data={breadcrumbs} />
						</BreadcrumbsWrapper>
					)}
					<MainGrid
						$withRelevance={!!apartData?.selectionInfo}
						$withStat={apartData?.selectionInfo?.selectionStats?.length > 0 || apartData?.stories}
						$withMortgage
						$withAdvert={apartAdvert?.length > 0}
						$withRelated={apartData?.similar?.length > 0}
					>
						<StickyBar
							handlerModalToggle={handlerModalToggle}
							handleScrollToMortgage={handleScrollToMortgage}
							mortgageData={mortgageModalData}
							isResale
						/>
						<ApartTitle
							apartTitle={apartTitle}
							data={house}
							withMap={withMap}
							handleMapClick={handleMapClick}
							city={city}
							cityTitle={cityTitle}
						/>
						<ContentSliderWrapper>
							<ImageSlider
								id={apartData.id}
								images={apartData.images}
								floorPlan={apartData.floorPlan}
								house={[house.photo, ...house.images]}
								layout={apartData.layout}
								isResale
							/>
						</ContentSliderWrapper>
						<PropsWrapper $isConnected={!isMobile}>
							<Props data={PROPS_APART} />
						</PropsWrapper>
						{apartAdvert?.length > 0 && <Advert apartAdvert={apartAdvert} />}
						{(apartData?.stories || apartData?.selectionInfo?.selectionStats?.length > 0) && (
							<DescriptionStatsWrapper>
								<TitleWithLogo>
									<TitleMax2>Аналитика</TitleMax2>
									<LogoWrapper>
										<LogoImg viewBox="0 0 315 68">{Logo}</LogoImg>
										<LogoText>Цифровой Риелтор</LogoText>
									</LogoWrapper>
								</TitleWithLogo>
								{isMobile && (
									<DescriptionTitle>
										Мы собрали все самое интересное об этой квартире, предварительно сравнив ее с
										другими, посмотрите, что получилось
									</DescriptionTitle>
								)}
								<DescriptionCharts
									selectionStories={apartData.stories}
									selectionStats={apartData.selectionInfo?.selectionStats ?? []}
								/>
							</DescriptionStatsWrapper>
						)}
						{isActive && (
							<MortgageWrapper id="mortgageBlock">
								<TitleMax2>Ипотека</TitleMax2>
								<Mortgage
									defaultPrice={apartData.priceTotalMin}
									apartID={apartData.id}
									handleCallbackClick={handleMortgageClick}
								/>
							</MortgageWrapper>
						)}
						{withMap && (
							<MapWrapper id="map" ref={mapRef}>
								<TitleMax2>Инфраструктура</TitleMax2>
								<MapBlock>
									<InfrastructureMap
										mode="apart"
										center={[house.latitude, house.longitude]}
										apartData={{
											apartName,
											buildingNum: null,
											isResale: true,
											windowAngle: onMap?.windowAngle,
										}}
										complexData={{
											address: house?.addressText,
											complexName: house?.name,
											isComplex: house?.isComplex,
										}}
										infrastructure={infrastructure?.items}
									/>
								</MapBlock>
							</MapWrapper>
						)}

						{hasHouseInfo && (
							<PropsWrapper $isBuilding>
								<TitleMax2>О доме</TitleMax2>
								<HouseProps data={PROPS_BUILDING} />
							</PropsWrapper>
						)}

						{!!apartData?.selectionInfo && (
							<Relevance selectionInfo={apartData.selectionInfo} city={city} />
						)}
					</MainGrid>
				</Container>
				{(apartData?.similar?.length > 0 || apartData?.lastVisited?.length > 0) && (
					<BottomRelatedRow>
						<BottomWrapper>
							<RelatedTabs>
								{apartData?.similar?.length > 0 && (
									<RelatedTab
										$isActive={activeTab === 'similar'}
										onClick={() => setActiveTab('similar')}
									>
										<span>Похожие квартиры</span>
									</RelatedTab>
								)}
								{apartData?.lastVisited?.length > 0 && (
									<RelatedTab
										$isActive={activeTab === 'lastViewed'}
										onClick={() => setActiveTab('lastViewed')}
									>
										<span>Вы недавно смотрели</span>
									</RelatedTab>
								)}
							</RelatedTabs>
							{activeTab === 'similar' && (
								<RelatedWrapper>
									<Related data={apartData.similar} city={city} isMobile={isMobile} />
								</RelatedWrapper>
							)}
							{activeTab === 'lastViewed' && (
								<ViewedWrapper>
									<Related data={apartData.lastVisited} city={city} isMobile={isMobile} />
								</ViewedWrapper>
							)}
						</BottomWrapper>
					</BottomRelatedRow>
				)}
			</Section>
			<Modal
				isShowing={currentModal}
				hide={closeModal}
				overflowEnabled={!isMobile && currentModal === 'innerCallback'}
			>
				<FormWrapper $fullWidth={currentModal === 'innerCallback' || currentModal === 'outerCallbackAdvert'}>
					<CallbackPopupSwitcher
						modalName={currentModal}
						mortgageData={mortgageModalData}
						apartId={apartData.id}
						handleCallbackModal={(modalName) => dispatch(setApartCurrentModal(modalName))}
					/>
				</FormWrapper>
			</Modal>
		</Root>
	);
};

export default ApartResale;
